// =====================================================================
// PLENARIUM - "Todos os Projetos" no painel do vereador (PLENARIUM:CARDS)
// Lista as materias da camara (busca, ano, tipo) e abre o arquivo
// principal e TODOS os documentos anexados (pareceres, emendas...).
// Usa so /api/projetos-camara (leitura).
// =====================================================================
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';

const TIPO = { PROJETO_LEI: 'Projeto de Lei', PROJETO_RESOLUCAO: 'Projeto de Resolução', PROJETO_DECRETO: 'Projeto de Decreto', REQUERIMENTO: 'Requerimento', MOCAO: 'Moção', INDICACAO: 'Indicação', OUTROS: 'Outros', ATA_ANTERIOR: 'Ata da Sessão Anterior' }; // PLENARIUM:ATA_MATERIA
const STATUS = {
  EM_TRAMITACAO: ['Em tramitação', '#1d4ed8', '#dbeafe'], APROVADO: ['Aprovado', '#15803d', '#dcfce7'], REPROVADO: ['Reprovado', '#b91c1c', '#fee2e2'],
  ARQUIVADO: ['Arquivado', '#475569', '#f1f5f9'], DEVOLVIDO: ['Devolvido', '#92400e', '#fef3c7'], PROMULGADO: ['Promulgado', '#6d28d9', '#ede9fe']
};
const ANEXO = { PROJETO: 'Projeto', PARECER_COMISSAO: 'Parecer de Comissão', PARECER_RELATOR: 'Parecer do Relator', EMENDA: 'Emenda', SUBSTITUTIVO: 'Substitutivo', REDACAO_FINAL: 'Redação Final', OFICIO: 'Ofício', OUTROS: 'Documento' };

// Abre o documento numa aba nova. A aba e aberta JA, no clique (senao o
// tablet bloqueia como propaganda); o PDF vira Blob (o leitor do Chrome
// recusa "data:"). Mesmo jeito do abrirAnexo do painel.
export async function abrirDocumento(url) {
  const aba = window.open('', '_blank');
  if (!aba) { toast.error('O navegador bloqueou a nova aba. Permita pop-ups para este site.'); return; }
  try { aba.document.title = 'Carregando documento...'; aba.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px;color:#475569">Carregando o documento...</p>'; } catch (e) { /* segue */ }
  try {
    const r = await axios.get(url);
    const dados = r.data && r.data.arquivo;
    if (!dados) { try { aba.close(); } catch (e) {} toast.error('Documento sem arquivo.'); return; }
    if (!dados.startsWith('data:')) { aba.location.href = dados; return; }
    const [cab, corpo] = dados.split(',');
    const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/pdf';
    const bin = atob(corpo); const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    aba.location.href = URL.createObjectURL(new Blob([bytes], { type: tipo }));
  } catch (e) {
    try { aba.close(); } catch (x) {}
    toast.error((e && e.response && e.response.data && e.response.data.error) || 'Não foi possível abrir o documento.');
  }
}

export default function TodosProjetos() {
  const [dados, setDados] = useState({ anos: [], projetos: [] });
  const [carregando, setCarregando] = useState(true);
  const [ano, setAno] = useState('');
  const [tipo, setTipo] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [aberto, setAberto] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const q = new URLSearchParams();
      if (ano) q.set('ano', ano); if (tipo) q.set('tipo', tipo); if (buscaAplicada) q.set('busca', buscaAplicada);
      const r = await axios.get(`/api/projetos-camara?${q.toString()}`);
      setDados(r.data || { anos: [], projetos: [] });
    } catch (e) { toast.error('Não foi possível carregar os projetos.'); }
    finally { setCarregando(false); }
  }, [ano, tipo, buscaAplicada]);

  useEffect(() => { carregar(); }, [carregar]);

  const campo = { border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: '1rem', background: '#fff', color: '#0f172a', fontFamily: 'inherit' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <form onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca.trim()); }} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por número, ementa ou autor" style={{ ...campo, flex: '2 1 220px' }} />
        <select value={ano} onChange={e => setAno(e.target.value)} style={{ ...campo, flex: '1 1 120px' }}>
          <option value="">Todos os anos</option>
          {dados.anos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ ...campo, flex: '1 1 160px' }}>
          <option value="">Todos os tipos</option>
          {Object.entries(TIPO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button type="submit" style={{ ...campo, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 800, cursor: 'pointer' }}>Buscar</button>
      </form>

      {carregando ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 30 }}>Carregando…</div>
      ) : !dados.projetos.length ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 36, textAlign: 'center', border: '1px solid #e2e8f0', color: '#64748b' }}>Nenhuma matéria encontrada.</div>
      ) : (
        <>
          <div style={{ color: '#64748b', fontSize: '.85rem' }}>{dados.projetos.length} matéria(s){dados.projetos.length === 300 ? ' — mostrando as 300 mais recentes; use a busca ou o ano para filtrar.' : ''}</div>
          {dados.projetos.map(p => {
            const st = STATUS[p.status] || [p.status, '#475569', '#f1f5f9'];
            const docs = (p.temPrincipal ? 1 : 0) + (p.anexos || []).length;
            const exp = aberto === p.id;
            return (
              <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <button type="button" onClick={() => setAberto(exp ? null : p.id)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', color: '#0f172a', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                      <b style={{ fontSize: '1.05rem' }}>{TIPO[p.tipo] || p.tipo} nº {String(p.numero).includes('/') ? p.numero : `${p.numero}/${p.ano}`}</b>
                      <span style={{ background: st[2], color: st[1], fontWeight: 800, fontSize: '.72rem', padding: '3px 8px', borderRadius: 999 }}>{st[0]}</span>
                    </div>
                    <div style={{ color: '#334155', fontSize: '.95rem', lineHeight: 1.45 }}>{p.ementa}</div>
                    <div style={{ color: '#64748b', fontSize: '.82rem', marginTop: 4 }}>Autor: {p.autor} · {docs} documento(s)</div>
                  </div>
                  {exp ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
                </button>
                {exp && (
                  <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 8, background: '#f8fafc' }}>
                    {!docs && <div style={{ color: '#64748b' }}>Nenhum documento anexado a esta matéria.</div>}
                    {p.temPrincipal && (
                      <button type="button" onClick={() => abrirDocumento(`/api/projetos-camara/${p.id}/principal`)} style={docBtn}>
                        <FileText size={18} color="#2563eb" /><span style={{ flex: 1 }}><b>Texto da matéria</b><br /><small style={{ color: '#64748b' }}>Arquivo principal</small></span><span style={abrir}>Abrir</span>
                      </button>
                    )}
                    {(p.anexos || []).map(a => (
                      <button key={a.id} type="button" onClick={() => abrirDocumento(`/api/projetos-camara/anexo/${a.id}`)} style={docBtn}>
                        <FileText size={18} color="#7c3aed" /><span style={{ flex: 1, minWidth: 0 }}><b>{ANEXO[a.tipo] || 'Documento'}</b><br /><small style={{ color: '#64748b' }}>{a.nome}</small></span><span style={abrir}>Abrir</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const docBtn = { display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit', color: '#0f172a' };
const abrir = { background: '#2563eb', color: '#fff', fontWeight: 800, fontSize: '.85rem', padding: '6px 12px', borderRadius: 8 };
